/**
 * MODULE: core/tenant/frontend/components/TenantGuard.tsx
 * PURPOSE: Tenant guard component - Conditionally renders children based on tenant status and features
 *
 * DEPENDENCIES:
 *   - react: ReactNode for children prop
 *   - ../hooks/useTenant: useTenant hook for tenant context
 *   - ../../contracts/tenant.types: TenantStatus, TenantFeatures types
 *
 * PUBLIC API:
 *   - TenantGuard: React component for conditional rendering based on tenant state
 *
 * SIDE EFFECTS:
 *   - Reads tenant context via useTenant hook
 *   - Conditionally renders children or fallback based on tenant status/features
 *   - Displays loading state while tenant is loading
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 62 / 200 lines
 *
 * CHANGE LOG:
 *   - 2026-01-20: Created - Tenant guard component for feature gating
 *     Guards routes/components based on tenant status (active, suspended, etc.)
 *     Guards routes/components based on tenant features
 *     Provides fallback UI for unauthorized access
 *     Related: TenantProvider context, useTenant hook
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /core/tenant/README.md
 */

import { ReactNode } from 'react';
import { useTenant } from '../hooks/useTenant';
import type { TenantStatus, TenantFeatures } from '../../contracts/tenant.types';

interface TenantGuardProps {
  children: ReactNode;
  requiredStatus?: TenantStatus[];
  requiredFeature?: keyof TenantFeatures;
  fallback?: ReactNode;
}

export function TenantGuard({
  children,
  requiredStatus = ['active'],
  requiredFeature,
  fallback,
}: TenantGuardProps) {
  const { currentTenant, isLoading } = useTenant();

  if (isLoading) {
    return (
      <div className="tenant-guard-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!currentTenant) {
    return fallback ? <>{fallback}</> : (
      <div className="tenant-guard-error">
        <h2>No Tenant Selected</h2>
        <p>Please select a tenant to continue.</p>
      </div>
    );
  }

  if (!requiredStatus.includes(currentTenant.status)) {
    return fallback ? <>{fallback}</> : (
      <div className="tenant-guard-error">
        <h2>Tenant Unavailable</h2>
        <p>This tenant is currently {currentTenant.status}.</p>
      </div>
    );
  }

  if (requiredFeature) {
    const features = currentTenant.settings?.features;
    const featureEnabled = features?.[requiredFeature];

    if (!featureEnabled) {
      return fallback ? <>{fallback}</> : (
        <div className="tenant-guard-error">
          <h2>Feature Not Available</h2>
          <p>This feature is not enabled for your tenant.</p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
